import React, { useRef, Dispatch, SetStateAction, RefObject } from 'react';
import {
  TableColumnWidthInfo,
  IntegratedGrouping,
} from '@devexpress/dx-react-grid';
import {
  VirtualTableProps,
  TableProps,
} from '@devexpress/dx-react-grid-material-ui';
import LinearProgress from '@material-ui/core/LinearProgress';
import clsx from 'clsx';
import {
  DataTypeProvidersProps,
  DirtyFormValues,
  RowSelectionConfig,
  SortingState,
  TableComponentProps,
  TableFilterFormComponent,
} from '../../../services/Main/types.Component';
import useStyles from './Table.styles';
import TableModules, {
  TableFilterFormProps,
} from '../../lowLevel/TableModules';
import PagingPanel from './components/PagingPanel';
import type { TableSelectionRef } from './components/TableSelectionPanel';
import TableViewComponent from './components/TableViewComponent';

const MIN_HEIGHT_FULL = 600;
const MIN_HEIGHT_COMPACT = 250;

interface TableComponentViewProps extends TableComponentProps {
  isLoading: boolean;
  tableProps: VirtualTableProps | TableProps;
  loadRows: () => void;
  dataTypeProvidersProps: DataTypeProvidersProps;
  setSorting: Dispatch<SetStateAction<SortingState | undefined>>;
  sortingStateColumnExtensions: any[];
  groupingStateColumnExtensions: IntegratedGrouping.ColumnExtension[];
  someColumnSortable: boolean;
  someColumnHasTitle: boolean;
  setQuery: Dispatch<SetStateAction<string | undefined>>;
  filter: DirtyFormValues | undefined;
  setFilter: Dispatch<SetStateAction<DirtyFormValues | undefined>>;
  columnWidths: TableColumnWidthInfo[];
  setColumnWidths: Dispatch<SetStateAction<TableColumnWidthInfo[]>>;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  setPageSize: Dispatch<SetStateAction<number>>;
  query?: string;
  columnOrder: string[];
  setColumnOrder: Dispatch<SetStateAction<string[]>>;
  hiddenColumnNames: string[];
  tableContentRef: RefObject<HTMLDivElement>;
  rowSelectionConfig?: RowSelectionConfig;
  tableSelectionStateRef?: RefObject<TableSelectionRef>;
  tableSelectionPanelRef?: RefObject<HTMLDivElement>;
}

export default ({
  columns,
  columnBands,
  rows,
  isLoading,
  heading,
  header,
  showSearchbar,
  filterComponent,
  quickFilters,
  requestConfig,
  actions,
  alerts,
  showTotalRows,
  tableProps,
  loadRows,
  dataTypeProvidersProps,
  setSorting,
  sortingStateColumnExtensions,
  groupingStateColumnExtensions,
  fixedColumns,
  someColumnHasTitle,
  someColumnSortable,
  fullHeight = true,
  query,
  setQuery,
  filter,
  setFilter,
  columnWidths,
  setColumnWidths,
  setCurrentPage,
  setPageSize,
  enableColumnResizing,
  columnOrder,
  setColumnOrder,
  enableColumnReordering,
  columnVisibilityConfig,
  hiddenColumnNames,
  maxHeight,
  rowSelectionConfig,
  tableSelectionStateRef,
  tableSelectionPanelRef,
  options: {
    totalRows,
    pageSize,
    currentPage,
    sorting,
    pageSizeOptions,
    pagingDisabled,
  },
  groupingConfig,
  tableContentRef,
}: TableComponentViewProps) => {
  const classes = useStyles();
  const rootRef = useRef<HTMLDivElement>(null);

  const handleFilterChange = (newFilterState: DirtyFormValues | undefined) => {
    setCurrentPage(0);
    // Мерджим прошлое состояние и новое - это нужно для того чтобы
    // корректно работали две формы:
    // 1. Которая расположена возле чипсов
    // 2. В Drawer
    setFilter((prevState) => ({
      ...prevState,
      ...newFilterState,
    }));
  };

  const tableFilterFormProps:
    | Omit<TableFilterFormProps, 'filterChips' | 'setFilterChips'>
    | undefined = filterComponent && {
    ...(filterComponent as TableFilterFormComponent),
    filter,
    setFilter: handleFilterChange,
  };

  const minHeight = fullHeight ? MIN_HEIGHT_FULL : MIN_HEIGHT_COMPACT;

  const isVirtualTable = rows.length > 500;

  return (
    <div
      className={classes.root}
      ref={rootRef}
      style={{
        height: fullHeight
          ? `calc(100vh - ${(rootRef.current?.offsetTop || 0) + 40}px)`
          : '100%',
        minHeight,
        maxHeight: maxHeight && maxHeight < minHeight ? minHeight : maxHeight,
        position: 'relative',
      }}
    >
      <TableModules
        totalRows={totalRows}
        heading={heading}
        header={header}
        showSearchbar={showSearchbar}
        searchInputProps={{ query, onQueryChange: setQuery }}
        tableFilterFormProps={tableFilterFormProps}
        requestConfig={requestConfig}
        actions={actions}
        alerts={alerts}
        showTotalRows={showTotalRows}
        quickFilters={quickFilters}
      />
      <div
        className={clsx(classes.tableContent, { isVirtualTable })}
        ref={tableContentRef}
      >
        <LinearProgress
          className={clsx(classes.progress, { active: isLoading })}
        />
        <TableViewComponent
          isVirtualTable={isVirtualTable}
          columns={columns}
          columnBands={columnBands}
          rows={rows}
          tableProps={tableProps}
          dataTypeProvidersProps={dataTypeProvidersProps}
          setSorting={setSorting}
          sortingStateColumnExtensions={sortingStateColumnExtensions}
          groupingStateColumnExtensions={groupingStateColumnExtensions}
          someColumnHasTitle={someColumnHasTitle}
          someColumnSortable={someColumnSortable}
          columnWidths={columnWidths}
          setColumnWidths={setColumnWidths}
          columnOrder={columnOrder}
          setColumnOrder={setColumnOrder}
          enableColumnResizing={enableColumnResizing}
          enableColumnReordering={enableColumnReordering}
          columnVisibilityConfig={columnVisibilityConfig}
          hiddenColumnNames={hiddenColumnNames}
          fixedColumns={fixedColumns}
          rowSelectionConfig={rowSelectionConfig}
          tableSelectionStateRef={tableSelectionStateRef}
          tableSelectionPanelRef={tableSelectionPanelRef}
          options={{ sorting }}
          groupingConfig={groupingConfig}
        />
      </div>
      <div ref={tableSelectionPanelRef} />
      {!pagingDisabled && (
        <PagingPanel
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          pageSizeOptions={pageSizeOptions}
          totalRows={totalRows}
          loadRows={loadRows}
        />
      )}
    </div>
  );
};
